import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Globe, MapPin } from 'lucide-react';
import type { Locality, Region } from '@shared/schema';
import { getGeographyTheme } from '@shared/geography-theme';

import { fetchJson } from '@/lib/queryClient';
import { useGeographyContext } from '@/hooks/useGeographyContext';
import { useTheme } from '@/hooks/useTheme';
import {
  buildScopedGeographyHref,
  formatGeographySlugLabel,
  resolveGeographySelectionFromUrl,
} from '@/lib/geography-navigation';
import { cn } from '@/lib/utils';

interface ScopeRegionOption {
  id: string;
  name: string;
  slug: string;
  type: 'physical' | 'online';
}

interface ScopeLocalityOption {
  id: string;
  name: string;
  slug: string;
  parentLocalityId?: string | null;
}

function ScopeChip({
  label,
  active,
  online = false,
  icon: Icon,
  onClick,
  testId,
  themeSlug,
}: {
  label: string;
  active: boolean;
  online?: boolean;
  icon?: typeof Globe;
  onClick: () => void;
  testId?: string;
  themeSlug?: string;
}) {
  const { theme: colorScheme } = useTheme();
  const isDark = colorScheme === 'dark';
  const geoTheme = themeSlug ? getGeographyTheme(themeSlug) : null;
  const activeColors = geoTheme ? (isDark ? geoTheme.colorsDark : geoTheme.colors) : null;
  const activeStyle =
    active && !online && activeColors
      ? {
          background: activeColors.primary,
          color: activeColors.primaryForeground,
          borderColor: activeColors.primary,
        }
      : undefined;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={active}
      aria-pressed={active}
      data-testid={testId}
      style={activeStyle}
      className={cn(
        'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-medium leading-none transition-colors disabled:cursor-default disabled:opacity-100',
        active &&
          online &&
          'border-sky-700 bg-sky-700 text-white dark:border-sky-500 dark:bg-sky-500',
        active && !online && 'border-primary bg-primary text-primary-foreground',
        !active &&
          online &&
          'border-sky-200 bg-sky-50 text-sky-700 hover:border-sky-300 hover:bg-sky-100 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-200 dark:hover:border-sky-700 dark:hover:bg-sky-900/40',
        !active &&
          !online &&
          'border-border/70 bg-card text-foreground hover:border-primary/40 hover:bg-primary/5'
      )}
    >
      {Icon ? <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" /> : null}
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

function ScopeSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 shrink-0 items-center gap-1.5">
      <p className="shrink-0 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </p>
      {/* On mobile each row scrolls independently; on sm+ overflow is visible (parent scrolls) */}
      <div className="min-w-0 overflow-x-auto overscroll-x-contain [scrollbar-width:none] sm:overflow-visible">
        <div className="flex min-w-max items-center gap-1.5">{children}</div>
      </div>
    </div>
  );
}

export default function GeographyScopeBar() {
  const [location, navigate] = useLocation();
  const [hiddenOnScroll, setHiddenOnScroll] = useState(false);
  const {
    regionSlug: persistedRegionSlug,
    localitySlug: persistedLocalitySlug,
    region: contextRegion,
    locality: contextLocality,
    setGeography,
  } = useGeographyContext();
  const hiddenOnScrollRef = useRef(false);
  const lastScrollY = useRef(0);
  const lastVisibleY = useRef(0);
  const lastHiddenY = useRef(0);
  const search = typeof window === 'undefined' ? '' : window.location.search;

  const resolvedLocation = useMemo(
    () =>
      resolveGeographySelectionFromUrl(location, search, {
        regionSlug: persistedRegionSlug,
        localitySlug: persistedLocalitySlug,
      }),
    [location, persistedLocalitySlug, persistedRegionSlug, search]
  );

  const { data: regions = [], isLoading: regionsLoading } = useQuery<Region[]>({
    queryKey: ['active-regions'],
    queryFn: () => fetchJson<Region[]>('/api/regions'),
    staleTime: 1000 * 60 * 10,
  });

  const regionOptions = useMemo<ScopeRegionOption[]>(() => {
    if (regions.length > 0) {
      return regions.map(region => ({
        id: region.id,
        name: region.name,
        slug: region.slug,
        type: region.type as 'physical' | 'online',
      }));
    }

    return [
      {
        id: resolvedLocation.regionSlug,
        name:
          contextRegion?.slug === resolvedLocation.regionSlug
            ? contextRegion.name
            : formatGeographySlugLabel(resolvedLocation.regionSlug),
        slug: resolvedLocation.regionSlug,
        type:
          contextRegion?.slug === resolvedLocation.regionSlug
            ? (contextRegion.type as 'physical' | 'online')
            : resolvedLocation.regionSlug === 'online'
              ? 'online'
              : 'physical',
      },
    ];
  }, [contextRegion, regions, resolvedLocation.regionSlug]);

  const activeRegion = useMemo(
    () => regionOptions.find(region => region.slug === resolvedLocation.regionSlug) ?? null,
    [regionOptions, resolvedLocation.regionSlug]
  );
  const isOnlineRegion = activeRegion?.type === 'online';

  const { data: localities = [], isLoading: localitiesLoading } = useQuery<Locality[]>({
    queryKey: ['scope-localities', resolvedLocation.regionSlug],
    queryFn: () => fetchJson<Locality[]>(`/api/regions/${resolvedLocation.regionSlug}/localities`),
    enabled: Boolean(resolvedLocation.regionSlug) && !isOnlineRegion,
    staleTime: 1000 * 60 * 10,
  });

  const localityOptions = useMemo<ScopeLocalityOption[]>(() => {
    if (localities.length > 0) {
      return localities.map(locality => ({
        id: locality.id,
        name: locality.name,
        slug: locality.slug,
        parentLocalityId: locality.parentLocalityId,
      }));
    }

    if (
      contextLocality &&
      contextRegion?.slug === resolvedLocation.regionSlug &&
      contextLocality.slug === resolvedLocation.localitySlug
    ) {
      return [
        {
          id: contextLocality.id,
          name: contextLocality.name,
          slug: contextLocality.slug,
        },
      ];
    }

    return [];
  }, [
    contextLocality,
    contextRegion,
    localities,
    resolvedLocation.localitySlug,
    resolvedLocation.regionSlug,
  ]);

  useEffect(() => {
    hiddenOnScrollRef.current = hiddenOnScroll;
  }, [hiddenOnScroll]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    let ticking = false;
    const HIDE_START_Y = 96;
    const HIDE_DISTANCE = 64;
    const SHOW_DISTANCE = 28;

    lastScrollY.current = window.scrollY;
    lastVisibleY.current = window.scrollY;
    lastHiddenY.current = window.scrollY;

    const updateVisibility = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastScrollY.current;

      if (currentY <= HIDE_START_Y) {
        if (hiddenOnScrollRef.current) {
          hiddenOnScrollRef.current = false;
          setHiddenOnScroll(false);
        }
        lastVisibleY.current = currentY;
        lastHiddenY.current = currentY;
      } else if (delta > 0) {
        if (hiddenOnScrollRef.current) {
          lastHiddenY.current = currentY;
        } else if (currentY - lastVisibleY.current >= HIDE_DISTANCE) {
          hiddenOnScrollRef.current = true;
          setHiddenOnScroll(true);
          lastHiddenY.current = currentY;
        }
      } else if (delta < 0) {
        if (hiddenOnScrollRef.current && lastHiddenY.current - currentY >= SHOW_DISTANCE) {
          hiddenOnScrollRef.current = false;
          setHiddenOnScroll(false);
          lastVisibleY.current = currentY;
        } else if (!hiddenOnScrollRef.current) {
          lastVisibleY.current = currentY;
        }
      }

      lastScrollY.current = currentY;
      ticking = false;
    };

    const handleScroll = () => {
      if (ticking) {
        return;
      }

      ticking = true;
      window.requestAnimationFrame(updateVisibility);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  function handleScopeChange(regionSlug: string, localitySlug?: string) {
    const href = buildScopedGeographyHref(
      location,
      search,
      { regionSlug, localitySlug },
      {
        regionSlug: persistedRegionSlug,
        localitySlug: persistedLocalitySlug,
      }
    );

    setGeography(regionSlug, localitySlug);
    navigate(href);
  }

  if (regionsLoading && regionOptions.length === 0) {
    return (
      <div className="overflow-hidden border-b border-border/60 bg-background/94 backdrop-blur-xl">
        <div className="mx-auto max-w-[var(--page-max-width)] px-4 py-1.5 sm:px-6">
          <div className="-mx-1 overflow-x-auto overscroll-x-contain px-1">
            <div className="flex min-w-max items-center gap-2">
              <div className="h-8 w-52 animate-pulse rounded-full border border-border/70 bg-card" />
              <div className="h-8 w-64 animate-pulse rounded-full border border-border/70 bg-card" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'overflow-hidden border-b border-border/60 bg-background/94 backdrop-blur-xl transition-[max-height,opacity,transform,border-color] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
        hiddenOnScroll
          ? 'pointer-events-none max-h-0 -translate-y-2 border-transparent opacity-0'
          : 'max-h-24 translate-y-0 opacity-100'
      )}
    >
      <div className="mx-auto max-w-[var(--page-max-width)] px-4 py-1.5 sm:px-6">
        <nav aria-label="Geography scope" data-testid="geography-scope-bar">
          <div className="sm:relative sm:-mx-1 sm:overflow-x-auto sm:overscroll-x-contain sm:px-1 sm:[scrollbar-width:none] sm:[mask-image:linear-gradient(to_right,transparent_0,black_8px,black_calc(100%-2rem),transparent_100%)]">
            <div className="flex flex-col gap-1.5 sm:min-w-max sm:flex-row sm:items-center sm:gap-2.5">
              <ScopeSection label="Market">
                {regionOptions.map(region => {
                  const active = region.slug === resolvedLocation.regionSlug;
                  const online = region.type === 'online';

                  return (
                    <ScopeChip
                      key={region.id}
                      label={region.name}
                      active={active}
                      online={online}
                      icon={online ? Globe : MapPin}
                      testId={`market-chip-${region.slug}`}
                      themeSlug={region.slug}
                      onClick={() => handleScopeChange(region.slug)}
                    />
                  );
                })}
              </ScopeSection>

              {!isOnlineRegion ? (
                <>
                  <div className="hidden h-4 w-px shrink-0 bg-border/60 sm:block" aria-hidden />
                  <ScopeSection label="Locality">
                    <ScopeChip
                      label="All"
                      active={!resolvedLocation.localitySlug}
                      icon={MapPin}
                      testId="locality-chip-all"
                      themeSlug={resolvedLocation.regionSlug}
                      onClick={() => handleScopeChange(resolvedLocation.regionSlug)}
                    />
                    {localitiesLoading && localityOptions.length === 0 ? (
                      <div className="h-8 w-32 animate-pulse rounded-full border border-border/70 bg-card" />
                    ) : (
                      (() => {
                        const activeIds = new Set(localityOptions.map(l => l.id));
                        const roots = localityOptions.filter(
                          l => !l.parentLocalityId || !activeIds.has(l.parentLocalityId)
                        );
                        const childrenOf = (id: string) =>
                          localityOptions.filter(l => l.parentLocalityId === id);

                        return roots.map(root => {
                          const children = childrenOf(root.id);
                          return (
                            <div key={root.id} className="flex items-center gap-1.5">
                              <ScopeChip
                                label={root.name}
                                active={root.slug === resolvedLocation.localitySlug}
                                icon={MapPin}
                                testId={`locality-chip-${root.slug}`}
                                themeSlug={root.slug}
                                onClick={() =>
                                  handleScopeChange(resolvedLocation.regionSlug, root.slug)
                                }
                              />
                              {children.length > 0 && (
                                <>
                                  <span
                                    className="text-[10px] text-muted-foreground/40 select-none"
                                    aria-hidden
                                  >
                                    ›
                                  </span>
                                  {children.map(child => (
                                    <ScopeChip
                                      key={child.id}
                                      label={child.name}
                                      active={child.slug === resolvedLocation.localitySlug}
                                      icon={MapPin}
                                      testId={`locality-chip-${child.slug}`}
                                      themeSlug={child.slug}
                                      onClick={() =>
                                        handleScopeChange(resolvedLocation.regionSlug, child.slug)
                                      }
                                    />
                                  ))}
                                </>
                              )}
                            </div>
                          );
                        });
                      })()
                    )}
                  </ScopeSection>
                </>
              ) : null}
            </div>
          </div>
        </nav>
      </div>
    </div>
  );
}
